// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryItem, { CategoryItemStyles } from "@plumberssupply/App/Components/Categories/CategoryItem";
import XngCarousel, { SwiperStyle } from "@plumberssupply/App/Components/XngCarousel/XngCarousel";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import { ellipsis, xScreen } from "@plumberssupply/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasCategoryContext;

interface SubCategoriesCarouselStyle {
    wrapper?: InjectableCss;
    swiperStyle?: SwiperStyle;
    categoryItemStyles?: CategoryItemStyles;
    carouselTitle?: TypographyProps;
}

export const centerSwiperSlideBreakPoint = 800;

const subCategoriesCarouselStyles: SubCategoriesCarouselStyle = {
    wrapper: {
        css: css`
            margin-bottom: 40px;
            width: 100%;
            .xng-pagination-container {
                margin-top: 24px;
            }
        `,
    },
    swiperStyle: {
        container: {
            css: css`
                ${xScreen("gte", "lg")} {
                    padding: 0 80px;
                    position: relative;
                }
            `,
        },
    },
    categoryItemStyles: {
        wrapper: {
            css: css`
                max-width: 136px;
                min-width: 136px;
                margin: 0 auto;
            `,
        },
        imageWrapper: {
            css: css`
                max-width: 100%;
                min-width: 136px;
                width: unset;
                height: 136px;
            `,
        },
        categoryTitle: {
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                ${ellipsis(2)}
            `,
        },
    },
    carouselTitle: {
        variant: "p",
        size: 14,
        weight: 400,
        lineHeight: "22px",
        css: css`
            margin-bottom: 16px;
        `,
    },
};

const styles = subCategoriesCarouselStyles;

const SubCategoriesCarousel = ({ category }: Props) => {
    if (!category || !category.subCategoryIds || !category.subCategoryIds?.length) {
        return null;
    }

    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: getXngCommon(state).isTabletScreen,
    }));

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.carouselTitle}>
                {translate("Found")} <b>{`${category.subCategoryIds.length} ${translate("Categories")}`}</b>{" "}
                {translate("under")} <b>{category.shortDescription}</b>
            </Typography>
            <XngCarousel
                extendedStyles={styles.swiperStyle}
                centerInsufficientSlides={false}
                maximumNumOfSlidesPerView={5}
                showSecondaryArrows={!isTabletScreen}
            >
                {category.subCategoryIds?.map(subCategoryId => (
                    <CategoryItem
                        key={subCategoryId}
                        categoryId={subCategoryId}
                        showImage={true}
                        extendedStyles={styles.categoryItemStyles}
                    />
                ))}
            </XngCarousel>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(SubCategoriesCarousel),
    definition: {
        group: "Categories",
        displayName: "Sub Categories Carousel",
        icon: "Carousel",
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
